/**
 * App Component - Main application entry point with routing
 * Requirements: 8.1, 8.2, 8.5, 8.6, 10.1, 10.2, 10.3, 10.4 - Application structure and navigation
 */
import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, SessionManager } from './features/auth';
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  QrLoginLandingPage,
} from './features/auth/pages';
import { QrIssuancePage } from './features/admin/pages/QrIssuancePage';
import {
  PrivacySettingsPage,
  PrivacyPolicyPage,
  TermsOfServicePage,
  DataUsageInfoPage,
} from './features/privacy';
import {
  AnalysisResultPage,
  MyResultsPage,
  CompanyResultsPage,
  IndividualReportPage,
  OrgReportDemoPage,
  OrgReportRealPage,
  OrgEvalEmbedPage,
  EmployeeListEmbedPage,
} from './features/analysis';
import { TerasuAnswerFlow, TerasuReport, TerasuReportDemoPage } from './features/terasu';
import { EmbeddedShell } from './features/embedded/EmbeddedShell';
import { EmbeddedQuestionnairePage } from './features/embedded/EmbeddedQuestionnairePage';
import { EmbeddedTerasuAnswerPage } from './features/embedded/EmbeddedTerasuAnswerPage';
import { EmployeeGb74AnswerPage } from './features/employee/EmployeeGb74AnswerPage';
import { EmployeeTerasuAnswerPage } from './features/employee/EmployeeTerasuAnswerPage';
import { AccountInfoPage } from './features/account/AccountInfoPage';
import { AggregationDashboard } from './features/aggregation';
import { CompanyNetworkPage } from './features/aggregation/components/CompanyNetworkPage';
import { OrgReportPdfPage } from './features/aggregation/components/OrgReportPdfPage';
import { PersonalReportPdfPage } from './features/analysis/components/PersonalReportPdfPage';
import { HistoryListPage } from './features/history';
import { QuestionSetManagementPage } from './features/admin/pages/QuestionSetManagementPage';
import { CompanyManagementPage } from './features/admin/pages/CompanyManagementPage';
import { DepartmentManagementPage } from './features/admin/pages/DepartmentManagementPage';
import { EmployeeManagementPage } from './features/admin/pages/EmployeeManagementPage';
import { PermissionManagementPage } from './features/admin/pages/PermissionManagementPage';
import OrgReportV3Page from './features/analysis/components/OrgReportV3Page';
import OrgReportEmbedV3Page from './features/analysis/components/OrgReportEmbedV3Page';
import {
  PersonalReportMeEmbedPage,
  PersonalReportUserEmbedPage,
} from './features/analysis/components/PersonalReportEmbedPage';
import UserManagementPage from './features/admin/pages/UserManagementPage';
import JudgmentPatternManagementPage from './features/admin/pages/JudgmentPatternManagementPage';
import TeamJudgmentPatternManagementPage from './features/admin/pages/TeamJudgmentPatternManagementPage';
import AnalysisResultManagementPage from './features/admin/pages/AnalysisResultManagementPage';
import DataSyncManagementPage from './features/admin/pages/DataSyncManagementPage';
import { AnswerCampaignManagementPage } from './features/admin/pages/AnswerCampaignManagementPage';
import { QuestionSetSelect } from './features/questionnaire/components/QuestionSetSelect';
import {
  fetchQuestionSets,
  fetchQuestionSetById,
  startSession,
  submitAnswersBulk,
  submitMultiFormatAnswersBulk,
  completeSession,
} from './features/questionnaire/api/questionnaireApi';
import type {
  PublishedQuestionSetSummary,
  QuestionSet,
  Question,
} from './features/questionnaire/types';
import { useParams } from 'react-router-dom';
import { AppLayout } from './shared/components/layout';
import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Button,
  LinearProgress,
  Alert,
  Stack,
} from '@mui/material';

// Loading component
const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

// Import SELF_ONLY Dashboard
import { SelfOnlyDashboard } from './features/dashboard/components/SelfOnlyDashboard';

// Dashboard component - Shows available question sets
// SELF_ONLY users see a custom dashboard design
const Dashboard = () => {
  const { user } = useAuth();

  // SELF_ONLYユーザーの場合は専用ダッシュボードを表示
  if (user?.permissionLevel === 'SELF_ONLY') {
    return <SelfOnlyDashboard />;
  }

  // その他のユーザー（DEPARTMENT, COMPANY_WIDE, Admin）向けダッシュボード。
  // 質問セット選択UI（「質問セットを選択」＋各ボタン）は非表示に変更（要望）。
  return (
    <Box>
      {/* 見出し「ダッシュボード」は AppLayout のページタイトル(h4)に集約したため、ここでは非表示 */}
      <Typography variant="body1" sx={{ mb: 3 }}>
        ようこそ！左のメニューからご希望の機能を選択してください。
      </Typography>
    </Box>
  );
};

// Coming Soon component for pages that need API integration
const ComingSoonPage = ({ title }: { title: string }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Typography variant="h5" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body1" color="text.secondary">
      この機能は現在開発中です。APIとの統合後にご利用いただけます。
    </Typography>
  </Box>
);

// Questionnaire Page - Shows question set selection or redirects to dashboard
const QuestionnairePage = () => {
  const navigate = useNavigate();
  const [questionSets, setQuestionSets] = useState<PublishedQuestionSetSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadQuestionSets = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchQuestionSets();
        setQuestionSets(data);
      } catch (err) {
        setError('質問セットの取得に失敗しました');
        console.error('Failed to fetch question sets:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadQuestionSets();
  }, []);

  const handleSelectQuestionSet = (qs: PublishedQuestionSetSummary) => {
    navigate(`/questionnaire/${qs.id}`);
  };

  return (
    <QuestionSetSelect
      questionSets={questionSets}
      isLoading={isLoading}
      error={error}
      onSelect={handleSelectQuestionSet}
    />
  );
};

// Import the new integrated questionnaire flow with GB人材 design
import { IntegratedQuestionnaireFlow } from './features/questionnaire/components/IntegratedQuestionnaireFlow';
import { Part5DemoPage } from './features/questionnaire/components/Part5DemoPage';
// パターンギャラリー(デモ)は seed(desc/tdesc)を同梱するため遅延ロードして初期バンドルから分離
const PersonalPatternsDemoPage = lazy(
  () => import('./features/analysis/components/PersonalPatternsDemoPage')
);
const OrgPatternsDemoPage = lazy(
  () => import('./features/analysis/components/OrgPatternsDemoPage')
);
import { TerasuWeeklyPatternsDemoPage } from './features/terasu/components/TerasuWeeklyPatternsDemoPage';
import { TerasuMonthlyPatternsDemoPage } from './features/terasu/components/TerasuMonthlyPatternsDemoPage';
// mfk-purchase-subsystem 公開購入フロー(P2・モック意匠5ステップ)
import { PurchasePlanPage } from './features/purchase/components/PurchasePlanPage';
import { PurchaseAccountPage } from './features/purchase/components/PurchaseAccountPage';
import { PurchaseConfirmPage } from './features/purchase/components/PurchaseConfirmPage';
import { PurchaseCompletePage } from './features/purchase/components/PurchaseCompletePage';
// 暫定LP (別チーム本番構築中のモック)
import { CorporateSitePage } from './features/landing/CorporateSitePage';
import { ServiceSitePage } from './features/landing/ServiceSitePage';
import type { UserAnswer, SubmitAnswerResponse } from './features/questionnaire/types';

// Questionnaire Answer Page - Shows questions for a specific set (fullscreen layout)
// Using IntegratedQuestionnaireFlow with GB人材 brand design
const QuestionnaireAnswerPage = () => {
  const { questionSetId } = useParams<{ questionSetId: string }>();
  const navigate = useNavigate();
  const [questionSet, setQuestionSet] = useState<QuestionSet | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadQuestionSet = async () => {
      if (!questionSetId) return;
      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchQuestionSetById(questionSetId);
        setQuestionSet(data);
        // Start session
        const session = await startSession(questionSetId);
        setSessionId(session.sessionId);
      } catch (err) {
        // 回答実施ガード: 既に回答済み / 有効な回答期間外 は専用メッセージで案内
        const code = (err as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data?.error;
        const serverMsg = (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message;
        if (code === 'ALREADY_ANSWERED') {
          setError(serverMsg || 'この回では既に回答済みです。レポートからご確認いただけます。');
        } else if (code === 'NO_ACTIVE_CAMPAIGN') {
          setError(
            serverMsg || '現在、回答を受け付けている回答実施がありません（回答期間外です）。'
          );
        } else {
          setError('質問セットの取得に失敗しました');
        }
        console.error('Failed to fetch question set:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadQuestionSet();
  }, [questionSetId]);

  const handleSubmitAnswers = async (answers: UserAnswer[]): Promise<SubmitAnswerResponse> => {
    if (!sessionId) {
      throw new Error('セッションIDが見つかりません');
    }

    try {
      // マルチフォーマット対応: scaleValueを持つ回答がある場合はmulti-formatエンドポイントを使用
      const hasScaleAnswers = answers.some((a) => a.scaleValue !== undefined);

      try {
        if (hasScaleAnswers) {
          const multiFormatAnswers = answers.map((answer) => ({
            questionId: answer.questionId,
            answerChoiceId: answer.answerChoiceId || null,
            scaleValue: answer.scaleValue !== undefined ? answer.scaleValue : null,
            departmentId: answer.departmentId || null,
          }));
          await submitMultiFormatAnswersBulk(sessionId, multiFormatAnswers);
        } else {
          // 旧形式（binary only）: 後方互換
          const bulkAnswers = answers.map((answer) => ({
            questionId: answer.questionId,
            answerChoiceId: answer.answerChoiceId || '',
          }));
          await submitAnswersBulk(sessionId, bulkAnswers);
        }
      } catch (bulkErr: any) {
        // SESSION_ALREADY_COMPLETED は回答保存済みなので無視して完了処理へ進む
        const errType = bulkErr?.response?.data?.error;
        if (errType !== 'SESSION_ALREADY_COMPLETED') {
          throw bulkErr;
        }
        console.log('[handleSubmitAnswers] Answers already saved (session completed), proceeding');
      }

      // Complete session
      try {
        const response = await completeSession(sessionId);
        return response;
      } catch (completeErr: any) {
        // SESSION_ALREADY_COMPLETED は既に完了済みなので成功扱い
        const errType = completeErr?.response?.data?.error;
        if (errType === 'SESSION_ALREADY_COMPLETED') {
          console.log('[handleSubmitAnswers] Session already completed, treating as success');
          return {
            sessionId,
            completedAt: new Date().toISOString(),
            totalAnswers: answers.length,
          };
        }
        throw completeErr;
      }
    } catch (err) {
      console.error('Failed to submit answers:', err);
      throw err;
    }
  };

  if (!questionSet) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        {isLoading ? (
          <CircularProgress />
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              px: 3,
              textAlign: 'center',
            }}
          >
            <Typography color="error">{error}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={() => navigate('/results/my')}>
                レポートを見る
              </Button>
              <Button variant="outlined" onClick={() => navigate('/')}>
                ホームへ
              </Button>
            </Box>
          </Box>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  const handleNavigateToService = () => {
    navigate('/');
  };

  const handleNavigateToMyPage = () => {
    navigate('/results/my');
  };

  return (
    <IntegratedQuestionnaireFlow
      questionSet={questionSet}
      onSubmit={handleSubmitAnswers}
      onNavigateToService={handleNavigateToService}
      onNavigateToMyPage={handleNavigateToMyPage}
      isLoading={isLoading}
      error={error}
      designVersion="redesign"
      enableResponsiveLayout={true}
      enableOrientationGuide={false}
      preferredOrientation="portrait"
      forceHorizontalOnMobile={false}
    />
  );
};

// My Results Page - imported from features/analysis

// Department Results Page - Needs API integration
const DepartmentResultsPage = () => <ComingSoonPage title="部門内分析結果一覧" />;

// Company Results Page - Using new design-compliant CompanyResultsPage
// CompanyResultsPage is imported from './features/analysis'

// Aggregation Dashboard Pages - Using AggregationDashboard (self-contained with internal API calls)
const DepartmentDistributionPage = () => <AggregationDashboard />;
const CompanyDistributionPage = () => <AggregationDashboard />;

// Network Graph Pages - Needs API integration
const DepartmentNetworkPage = () => <ComingSoonPage title="部門内ネットワーク図" />;
// CompanyNetworkPage is imported from './features/aggregation/components/CompanyNetworkPage'

// Question Set Management Page - imported from features/admin/pages
// Company/Department/Employee/Permission Management Pages - imported from features/admin/pages

// Bulk Registration Page - Using BulkRegistration component
import { BulkRegistration } from './features/admin/components/BulkRegistration';

const BulkRegistrationPage = () => {
  const { user } = useAuth();
  // Use user's company ID if available, otherwise use a default for admin
  const companyId = user?.companyId || '';

  return <BulkRegistration companyId={companyId} />;
};

// 403 Forbidden page
const ForbiddenPage = () => (
  <Box sx={{ textAlign: 'center', py: 4 }}>
    <Typography variant="h4" color="error" gutterBottom>
      403 Forbidden
    </Typography>
    <Typography variant="body1">このページにアクセスする権限がありません。</Typography>
  </Box>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }: { children?: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children || <Outlet />}</>;
};

// Public Route wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Permission-based route guard
const PermissionRoute = ({
  requiredPermission,
  adminOnly = false,
  children,
}: {
  requiredPermission?: 'SELF_ONLY' | 'DEPARTMENT' | 'COMPANY_WIDE';
  adminOnly?: boolean;
  children: React.ReactNode;
}) => {
  const { user } = useAuth();

  const PERMISSION_HIERARCHY = {
    SELF_ONLY: 0,
    DEPARTMENT: 1,
    COMPANY_WIDE: 2,
  };

  if (adminOnly && !user?.isAdmin) {
    return <ForbiddenPage />;
  }

  if (requiredPermission && user?.permissionLevel) {
    const userLevel = PERMISSION_HIERARCHY[user.permissionLevel] ?? -1;
    const requiredLevel = PERMISSION_HIERARCHY[requiredPermission] ?? 0;

    if (userLevel < requiredLevel) {
      return <ForbiddenPage />;
    }
  }

  return <>{children}</>;
};

function App() {
  return (
    <SessionManager>
      <Routes>
        {/* Public routes - no layout */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />

        {/*
          QR ログインランディング（公開ルート / 認証ガード外）。
          未認証端末からの到達を前提とするため PublicRoute/ProtectedRoute では包まず、
          そのまま描画する。消費・自動ログイン完了後にページ自身が /dashboard へ遷移する。
        */}
        <Route path="/qr-login" element={<QrLoginLandingPage />} />

        {/* モバイルWebView埋め込み用 (embedded=1・ProtectedRoute外・ネイティブからtoken注入) */}
        {/* モバイルアプリの「個人レポート」が開く。2026-08-22 に v2 のモバイル版へ差し替えた。
            旧実装(MyResultsPage・回答履歴の一覧)は /embed/report/me-legacy に残してある。 */}
        <Route
          path="/embed/report/me"
          element={
            <EmbeddedShell>
              <PersonalReportMeEmbedPage />
            </EmbeddedShell>
          }
        />
        <Route
          path="/embed/report/me-legacy"
          element={
            <EmbeddedShell>
              <MyResultsPage />
            </EmbeddedShell>
          }
        />
        {/* モバイルアプリの「GB組織」タブが開く。2026-08-22 に v3 へ差し替えた。
            旧実装(OrgReportRealPage)は /embed/report/org-legacy に残してある。 */}
        <Route
          path="/embed/report/org"
          element={
            <EmbeddedShell>
              <OrgReportEmbedV3Page />
            </EmbeddedShell>
          }
        />
        <Route
          path="/embed/report/org-legacy"
          element={
            <EmbeddedShell>
              <OrgReportRealPage />
            </EmbeddedShell>
          }
        />
        <Route
          path="/embed/home/org-eval"
          element={
            <EmbeddedShell>
              <OrgEvalEmbedPage />
            </EmbeddedShell>
          }
        />
        {/* 社長がモバイルの社員一覧から開く個人レポート。同じく v2 のモバイル版。
            旧実装(IndividualReportPage)は /embed/report/employee-legacy/:userId に残してある。 */}
        <Route
          path="/embed/report/employee/:userId"
          element={
            <EmbeddedShell>
              <PersonalReportUserEmbedPage />
            </EmbeddedShell>
          }
        />
        <Route
          path="/embed/report/employee-legacy/:userId"
          element={
            <EmbeddedShell>
              <IndividualReportPage />
            </EmbeddedShell>
          }
        />
        <Route
          path="/embed/report/employees"
          element={
            <EmbeddedShell>
              <EmployeeListEmbedPage />
            </EmbeddedShell>
          }
        />
        <Route
          path="/embed/terasu/report/weekly"
          element={
            <EmbeddedShell>
              <TerasuReport mode="weekly" />
            </EmbeddedShell>
          }
        />
        <Route
          path="/embed/terasu/report/monthly"
          element={
            <EmbeddedShell>
              <TerasuReport mode="monthly" />
            </EmbeddedShell>
          }
        />
        <Route
          path="/embed/questionnaire"
          element={
            <EmbeddedShell>
              <EmbeddedQuestionnairePage />
            </EmbeddedShell>
          }
        />
        <Route
          path="/embed/terasu/answer"
          element={
            <EmbeddedShell>
              <EmbeddedTerasuAnswerPage />
            </EmbeddedShell>
          }
        />

        {/* DEV専用 公開デモ (フロント完結・認証不要・ダミーデータ)。実運用前に削除可 */}
        <Route path="/demo/org-report" element={<OrgReportDemoPage />} />
        <Route path="/demo/terasu-report" element={<TerasuReportDemoPage />} />
        <Route path="/demo/part5" element={<Part5DemoPage />} />
        <Route
          path="/demo/personal-patterns"
          element={
            <Suspense fallback={<div style={{ padding: 24 }}>読み込み中…</div>}>
              <PersonalPatternsDemoPage />
            </Suspense>
          }
        />
        <Route
          path="/demo/org-patterns"
          element={
            <Suspense fallback={<div style={{ padding: 24 }}>読み込み中…</div>}>
              <OrgPatternsDemoPage />
            </Suspense>
          }
        />
        <Route path="/demo/terasu-weekly" element={<TerasuWeeklyPatternsDemoPage />} />
        <Route path="/demo/terasu-monthly" element={<TerasuMonthlyPatternsDemoPage />} />

        {/* 暫定LP (別チーム本番構築中のモック・画像表示のみ)。/corporate→/service→/purchase */}
        <Route path="/corporate" element={<CorporateSitePage />} />
        <Route path="/service" element={<ServiceSitePage />} />

        {/* 公開購入フロー (mfk-purchase-subsystem P2・ログイン不要・5ステップ) */}
        <Route path="/purchase" element={<PurchasePlanPage />} />
        <Route path="/purchase/account" element={<PurchaseAccountPage />} />
        <Route path="/purchase/confirm" element={<PurchaseConfirmPage />} />
        <Route path="/purchase/complete" element={<PurchaseCompletePage />} />

        {/* Questionnaire answer page - fullscreen without AppLayout */}
        <Route
          path="/questionnaire/:questionSetId"
          element={
            <ProtectedRoute>
              <QuestionnaireAnswerPage />
            </ProtectedRoute>
          }
        />

        {/* 社員Web版 回答フロー（縦・通常セッション・サイドバー無し） */}
        <Route
          path="/answer/gb74"
          element={
            <ProtectedRoute>
              <EmployeeGb74AnswerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/answer/terasu"
          element={
            <ProtectedRoute>
              <EmployeeTerasuAnswerPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes - with AppLayout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route path="/dashboard" element={<Dashboard />} />

          {/* SELF_ONLY routes (all authenticated users) */}
          <Route path="/questionnaire" element={<QuestionnairePage />} />
          <Route path="/analysis/my-results" element={<MyResultsPage />} />
          {/* GBなび 従業員 週次回答 (全員) */}
          <Route path="/terasu/answer" element={<TerasuAnswerFlow />} />
          {/* GB組織レポート (実データ: 会社全メンバーの gb74 結果を集計) */}
          {/* 旧b7の組織レポート。左メニューからは外したが、v3と見比べられるよう残置する
              （モバイルの /embed/report/org-legacy と同じ扱い）。 */}
          <Route path="/analysis/org-report" element={<OrgReportPdfPage />} />
          {/* 組織レポート v3（現行）。左メニュー「組織レポート」はここを指す。
              モバイルの /embed/report/org と算出・文言はまったく同じモジュールを使う。 */}
          <Route path="/analysis/org-report-v3" element={<OrgReportV3Page />} />
          {/* 組織レポート デモ (20名ダミー回答→集約→描画)。:sessionId より前に置く */}
          <Route path="/analysis/org-report-demo" element={<OrgReportDemoPage />} />
          {/* GBなび レポート デモ (週報/月報をフロントダミーで確認) */}
          <Route path="/terasu/report-demo" element={<TerasuReportDemoPage />} />
          <Route path="/analysis/:sessionId" element={<AnalysisResultPage />} />

          {/* DEPARTMENT routes */}
          <Route
            path="/aggregation/department/distribution"
            element={
              <PermissionRoute requiredPermission="DEPARTMENT">
                <DepartmentDistributionPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/aggregation/department/network"
            element={
              <PermissionRoute requiredPermission="DEPARTMENT">
                <DepartmentNetworkPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/analysis/department-results"
            element={
              <PermissionRoute requiredPermission="DEPARTMENT">
                <DepartmentResultsPage />
              </PermissionRoute>
            }
          />

          {/* COMPANY_WIDE routes */}
          {/* GBなび 経営者レポート: 週報 / 月報 を別画面に分割 */}
          <Route path="/terasu/report" element={<Navigate to="/terasu/report/weekly" replace />} />
          <Route
            path="/terasu/report/weekly"
            element={
              <PermissionRoute requiredPermission="COMPANY_WIDE">
                <TerasuReport mode="weekly" />
              </PermissionRoute>
            }
          />
          <Route
            path="/terasu/report/monthly"
            element={
              <PermissionRoute requiredPermission="COMPANY_WIDE">
                <TerasuReport mode="monthly" />
              </PermissionRoute>
            }
          />
          {/* アカウント情報（社長向け・COMPANY_WIDE）: 支払明細＋QR発行日 */}
          <Route
            path="/account"
            element={
              <PermissionRoute requiredPermission="COMPANY_WIDE">
                <AccountInfoPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/aggregation/company/distribution"
            element={
              <PermissionRoute requiredPermission="COMPANY_WIDE">
                <CompanyDistributionPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/aggregation/company/network"
            element={
              <PermissionRoute requiredPermission="COMPANY_WIDE">
                <CompanyNetworkPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/analysis/company-results"
            element={
              <PermissionRoute requiredPermission="COMPANY_WIDE">
                <CompanyResultsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/analysis/individual-report/:userId"
            element={
              <PermissionRoute requiredPermission="COMPANY_WIDE">
                <IndividualReportPage />
              </PermissionRoute>
            }
          />

          {/* 個人レポート(Web管理者向け): A4横2ページをHTML表示し、右上ボタンでPDF保存。
              モバイルWebViewが読む /embed/report/employee/:userId は縦1カラムのまま。 */}
          <Route
            path="/analysis/personal-report/:userId"
            element={
              <PermissionRoute requiredPermission="COMPANY_WIDE">
                <PersonalReportPdfPage />
              </PermissionRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/question-sets"
            element={
              <PermissionRoute adminOnly>
                <QuestionSetManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/companies"
            element={
              <PermissionRoute adminOnly>
                <CompanyManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/departments"
            element={
              <PermissionRoute adminOnly>
                <DepartmentManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/employees"
            element={
              <PermissionRoute adminOnly>
                <EmployeeManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/permissions"
            element={
              <PermissionRoute adminOnly>
                <PermissionManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/bulk-registration"
            element={
              <PermissionRoute adminOnly>
                <BulkRegistrationPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PermissionRoute adminOnly>
                <UserManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/qr-issuance"
            element={
              <PermissionRoute adminOnly>
                <QrIssuancePage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/judgment-patterns"
            element={
              <PermissionRoute adminOnly>
                <JudgmentPatternManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/team-judgment-patterns"
            element={
              <PermissionRoute adminOnly>
                <TeamJudgmentPatternManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/analysis-results"
            element={
              <PermissionRoute adminOnly>
                <AnalysisResultManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/data-sync"
            element={
              <PermissionRoute adminOnly>
                <DataSyncManagementPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/answer-campaigns"
            element={
              <PermissionRoute adminOnly>
                <AnswerCampaignManagementPage />
              </PermissionRoute>
            }
          />

          {/* Privacy routes (all authenticated users) */}
          <Route path="/privacy/settings" element={<PrivacySettingsPage />} />
          <Route path="/privacy/policy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy/data-usage" element={<DataUsageInfoPage />} />

          {/* 403 Forbidden page */}
          <Route path="/forbidden" element={<ForbiddenPage />} />
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </SessionManager>
  );
}

export default App;
